import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Registration } from 'src/app/models/registration';
import {
  ExtendedSearchReqResponse,
  PatternSearchResponse,
  SearchReqResponse,
  SearchService,
} from 'src/app/services/search-service';
import { RegistrationService } from 'src/app/services/registration-service';
import { DatelessSearchResponse } from '../../../dateless-search-page/dateless-search-page.component';
import {
  PlateKnowledge,
  PlateKnowledgeService,
} from 'src/app/services/plate-knowledge.service';


// A single "Similar registrations for sale" card. Every field is read from the
// EXISTING Registration search-result model — nothing is invented (there is no
// per-plate listing count on Registration, so we do not show one).
interface SimilarCard {
  registration: string;
  formattedRegistration: string;
  // Pence (Registration.price convention). Only set when the plate is
  // available (price >= 0); rendered as "from £x / 100".
  pricePence: number;
  // Deterministic, optional reason label. Omitted when it cannot be derived.
  reason: string | null;
  // The search term to carry forward (keeps "Same search" context on click).
  term: string | null;
  // The result's own format key ('current'/'prefix'/'suffix'/'dateless'/null),
  // retained so the reason label can be recomputed if our formatKey input
  // arrives after the (fast) search has already produced the cards.
  styleKey: string | null;
}

// "Similar registrations for sale" — reuses the EXISTING PlateX search logic
// (SearchService) rather than introducing a new similarity engine or backend.
//
// Priority order (per spec):
//   1. If we arrived from a search (this.searchTerm present), reuse that result
//      set via searchRaw() — which serves the in-memory cached last search with
//      ZERO network calls when the term matches what the user just searched.
//   2. Direct loads fall back to the existing pattern search (searchPattern) for
//      the registration; dateless plates prefer the dateless bucket.
//   3. Candidates are filtered (exclude self, available only, dedupe), sorted by
//      the existing relevance order, and capped to the first 4.
//
// Results are cached per-registration for the session so returning to the same
// plate does not re-run the request.
@Component({
  selector: 'pla-pdv2-similar',
  templateUrl: './similar.component.html',
  styleUrls: ['./similar.component.scss'],
})
export class SimilarComponent implements OnChanges {
  @Input() public registration: string = '';
  @Input() public formattedRegistration: string = '';
  @Input() public searchTerm: string | null = null;
  // 'current' | 'prefix' | 'suffix' | 'dateless' | 'ni' | null — used only for
  // deterministic reason labels; never to fabricate data.
  @Input() public formatKey: string | null = null;

  public loading: boolean = true;
  public cards: SimilarCard[] = [];
  // Surfaced when the dateless fallback could not use a structured arrangement
  // query (no such endpoint exists yet) — keeps the limitation honest.
  public note: string | null = null;
  // What this plate reads as (plate knowledge base); null when PlateX knows no
  // strong reading. Drives the "Also reads ..." cards that come first.
  public reading: string | null = null;
  // Reading cards found before the existing search runs; merged in front of
  // its cards by commit().
  private prefill: SimilarCard[] = [];

  // Session cache, keyed by the compact registration. Survives in-app
  // navigation so revisiting the same plate does not re-run the search.
  private static cache: Map<string, SimilarCard[]> = new Map<
    string,
    SimilarCard[]
  >();

  private readonly limit: number = 4;

  // The mode the current cards were built with — retained so the reason labels
  // can be recomputed in place if `formatKey` arrives after the search has
  // already produced the cards (no refetch).
  private mode: 'search' | 'format' | 'cohort' = 'format';

  constructor(
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private knowledge: PlateKnowledgeService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['registration'] && this.registration) {
      this.load();
      return;
    }
    // `formatKey` derives from the plate detail, which can load AFTER the (fast)
    // similar search has already rendered. When it changes and we already have
    // cards, recompute the deterministic reason labels in place — no network.
    if (changes['formatKey'] && this.cards.length) {
      this.relabel();
    }
  }

  // Recompute reason labels for the existing cards using the now-known
  // formatKey. In-memory only; updates the session cache too.
  private relabel(): void {
    // Only generic format-mode cards derive their label from formatKey. Search
    // ('Same search') and dateless-cohort ('Shares the letter/number …') labels
    // are independent of it, so leave those untouched.
    if (this.mode !== 'format') return;
    let changed = false;
    for (const c of this.cards) {
      if (this.reading && c.term === this.reading) continue; // reading cards keep their label
      const next = this.reasonForStyle(c.styleKey);
      if (next !== c.reason) {
        c.reason = next;
        changed = true;
      }
    }
    if (changed) {
      // Re-store so a later revisit reads the labelled cards from cache.
      SimilarComponent.cache.set(this.compactReg, this.cards);
    }
  }

  private get compactReg(): string {
    return (this.registration || '').replace(/\s+/g, '').toUpperCase();
  }

  private load(): void {
    const key = this.compactReg;
    if (!key) {
      this.loading = false;
      return;
    }

    const cached = SimilarComponent.cache.get(key);
    if (cached) {
      this.cards = cached;
      this.loading = false;
      return;
    }

    this.loading = true;
    this.cards = [];
    this.note = null;
    this.reading = null;
    this.prefill = [];

    // Reading first (plate knowledge base): other plates for sale that read
    // the same (HMZ4 -> HAMZA -> HAM 241, HA11 MZA), then the existing logic.
    this.loadReading(key, () => this.loadExisting(key));
  }

  // Look up the plate's reading and, if there is one, the plates for sale that
  // read the same (the existing search_v3 for the reading). Always calls next().
  // dev2 / local only, like the assistant widget; any failure just skips it.
  private loadReading(key: string, next: () => void): void {
    if (!this.knowledge.enabled) {
      next();
      return;
    }
    // Shared with the hero description: one request per plate.
    this.knowledge.get(key).then((kn: PlateKnowledge | null) => {
      if (key !== this.compactReg) return; // navigated away
      const first = kn && kn.readings && kn.readings.length ? kn.readings[0] : null;
      if (!first || !first.reads_as) {
        next();
        return;
      }
      const reading = first.reads_as;
      this.searchService.searchV3(
        reading,
        (res: SearchReqResponse) => {
          if (key !== this.compactReg) return;
          const found = ((res && res.registrations) || []).filter(
            (r: any) => (r.percentage || 0) >= 80
          );
          this.prefill = this.toReadingCards(key, found, reading);
          this.reading = this.prefill.length ? reading : null;
          next();
        },
        false
      );
    });
  }

  // Reading results -> cards: available only, exclude self, a shorter plate is
  // the premium version of the same reading.
  private toReadingCards(
    key: string,
    raw: Registration[],
    reading: string
  ): SimilarCard[] {
    const out: SimilarCard[] = [];
    const seen = new Set<string>([key]);
    const formatted = this.registrationService.formatRegistrations(
      (raw || []).slice()
    );
    for (const r of formatted) {
      if (!r || !r.registration) continue;
      const rc = r.registration.replace(/\s+/g, '').toUpperCase();
      if (seen.has(rc) || !(r.price > 0)) continue;
      seen.add(rc);
      out.push({
        registration: r.registration,
        formattedRegistration: r.formattedRegistration || r.registration,
        pricePence: r.price,
        reason:
          rc.length < key.length
            ? `Shorter ${reading} plate`
            : `Also reads ${reading}`,
        term: reading,
        styleKey: this.styleKey(r.style),
      });
      if (out.length >= this.limit) break;
    }
    return out;
  }

  // Reading cards first, then the existing cards, deduped and capped; cached.
  private commit(key: string, cards: SimilarCard[]): void {
    const seen = new Set<string>();
    const merged: SimilarCard[] = [];
    for (const c of [...this.prefill, ...cards]) {
      const rc = c.registration.replace(/\s+/g, '').toUpperCase();
      if (seen.has(rc)) continue;
      seen.add(rc);
      merged.push(c);
      if (merged.length >= this.limit) break;
    }
    this.cards = merged;
    SimilarComponent.cache.set(key, merged);
    this.loading = false;
  }

  // The original "Similar for sale" logic (dateless cohort, extended search,
  // pattern fallback). Its cards follow any reading cards.
  private loadExisting(key: string): void {
    // A full row of reading cards needs nothing else.
    if (this.prefill.length >= this.limit) {
      this.commit(key, []);
      return;
    }

    // Dateless plates: build the "Similar for sale" set from the plate's own
    // arrangement COHORT — other for-sale dateless plates that share its letter
    // group (e.g. ??K) or its number group (e.g. 72?). Detected structurally
    // (current/prefix/suffix carry letters at both ends, so they never match),
    // which means it works even before the formatKey input resolves. Powered by
    // the EXISTING dateless-search endpoint — no new backend. NI dateless is
    // left to the generic path.
    if (this.formatKey !== 'ni') {
      const dl = this.parseDateless(key);
      if (dl) {
        this.loadDatelessCohort(key, dl.letters, dl.numbers);
        return;
      }
    }

    // Priority 1 (search context) and Priority 2 (direct load) both reuse the
    // EXISTING `extendedSearch` primitive — the same fast endpoint the search
    // results page uses for available, priced comparables. (We deliberately do
    // NOT use searchRaw/search_v2: that live-search path is slow/504-prone.)
    const fromSearch = !!(this.searchTerm && this.searchTerm.trim());
    const term = fromSearch ? (this.searchTerm as string).trim() : key;
    const style = this.styleForExtended(this.formatKey);

    this.searchService.extendedSearch(
      term,
      style,
      (groups: ExtendedSearchReqResponse[]) => {
        const flat = (groups || []).reduce<Registration[]>(
          (acc, g) => acc.concat(g && g.results ? g.results : []),
          []
        );
        if (flat.length) {
          this.finish(key, flat, fromSearch ? 'search' : 'format');
          return;
        }
        // Empty (e.g. dateless has no structured coverage yet) → report the
        // limitation and fall back to the existing pattern search.
        if (this.formatKey === 'dateless') {
          this.note =
            'Structured dateless matching is not available yet; showing the closest matches.';
        }
        this.patternFallback(key, fromSearch);
      }
    );
  }

  // Map our format key to the `style` argument the extended-search endpoint
  // expects. Defaults to 'current' for anything we cannot map.
  private styleForExtended(fmt: string | null): string {
    if (fmt === 'prefix' || fmt === 'suffix' || fmt === 'dateless') {
      return fmt;
    }
    return 'current';
  }

  // Direct-load fallback using the EXISTING pattern endpoint. For dateless
  // plates we prefer the dateless bucket; otherwise we merge the format buckets
  // in relevance order.
  private patternFallback(key: string, fromSearch: boolean): void {
    const mode: 'search' | 'format' = fromSearch ? 'search' : 'format';
    this.searchService.searchPattern(
      this.compactReg,
      (res: PatternSearchResponse) => {
        if (!res) {
          this.finish(key, [], mode);
          return;
        }
        let merged: Registration[];
        if (this.formatKey === 'dateless') {
          // No structured "same arrangement + same letter" endpoint exists yet,
          // so we use the closest available signal — the dateless pattern
          // bucket — and note the limitation.
          merged = [
            ...(res.dateless || []),
            ...(res.current || []),
            ...(res.prefix || []),
            ...(res.suffix || []),
          ];
          if (!this.note) {
            this.note =
              'Showing the closest dateless matches. Structured arrangement matching is not available yet.';
          }
        } else {
          merged = [
            ...(res.current || []),
            ...(res.prefix || []),
            ...(res.suffix || []),
            ...(res.dateless || []),
          ];
        }
        this.finish(key, merged, mode);
      }
    );
  }

  // Shared finishing pipeline: format -> exclude self -> available only ->
  // dedupe -> keep existing relevance order -> cap -> map to cards -> cache.
  private finish(
    key: string,
    raw: Registration[],
    mode: 'search' | 'format'
  ): void {
    // formatRegistrations mutates + sorts by score (existing relevance order)
    // and sets `available = price >= 0`. Copy first so we never mutate a cached
    // SearchService result array.
    this.mode = mode;
    const formatted = this.registrationService.formatRegistrations(
      (raw || []).slice()
    );

    const seen = new Set<string>();
    const cards: SimilarCard[] = [];
    for (const r of formatted) {
      if (!r || !r.registration) continue;
      const rc = r.registration.replace(/\s+/g, '').toUpperCase();
      if (rc === key) continue; // exclude the current registration
      if (seen.has(rc)) continue; // dedupe
      if (!(r.price >= 0)) continue; // available/listed only — never invent a price
      seen.add(rc);
      const rk = this.styleKey(r.style);
      cards.push({
        registration: r.registration,
        formattedRegistration: r.formattedRegistration || r.registration,
        pricePence: r.price,
        reason: this.reasonFor(rk, mode),
        term: (this.searchTerm || '').trim() || null,
        styleKey: rk,
      });
      if (cards.length >= this.limit) break;
    }

    this.commit(key, cards);
  }

  // --- Dateless arrangement cohort -----------------------------------------
  //
  // A dateless plate has letters at ONE end and numbers at the other (e.g. 72K
  // = digits 72 + letter K, or K72 = letter K + digits 72). Current/prefix/
  // suffix plates carry letters at BOTH ends, so this structural parse returns
  // null for them — the cohort path only ever runs for genuine dateless plates,
  // even before the formatKey input resolves.
  private parseDateless(
    compact: string
  ): { letters: string; numbers: string } | null {
    let m = /^(\d+)([A-Z]+)$/.exec(compact); // number-first: 72K
    if (m) return { numbers: m[1], letters: m[2] };
    m = /^([A-Z]+)(\d+)$/.exec(compact); // letter-first: K72
    if (m) return { letters: m[1], numbers: m[2] };
    return null;
  }

  // Build the dateless-search layout mask the EXISTING endpoint expects:
  // one '*' per letter, a single 'x' separator, one '-' per number.
  // 72K (1 letter + 2 numbers) -> '*x--'.
  private datelessMask(letters: string, numbers: string): string {
    return '*'.repeat(letters.length) + 'x' + '-'.repeat(numbers.length);
  }

  // For-sale comparables from the plate's arrangement cohort, via the EXISTING
  // dateless-search endpoint (no new backend). Two cheap queries on the same
  // mask: one keyed by the letter group (??K) and one by the number group
  // (72?). Their available results are interleaved so neither axis dominates.
  private loadDatelessCohort(
    key: string,
    letters: string,
    numbers: string
  ): void {
    const mask = this.datelessMask(letters, numbers);
    // Canonical base: the dev2 build rewrites the standard plato-search
    // placeholder to Mode 2, which does not implement `process=dateless`
    // (returns `{}`). The canonical pair always reaches the real plato-search
    // Lambda, on dev2 and in production alike.
    this.searchService.searchDatelessLengthCanonical(
      mask,
      letters,
      (letterRes: DatelessSearchResponse) => {
        this.searchService.searchDatelessLengthCanonical(
          mask,
          numbers,
          (numberRes: DatelessSearchResponse) => {
            this.finishDatelessCohort(
              key,
              letterRes && letterRes.main ? letterRes.main : [],
              letters,
              numberRes && numberRes.main ? numberRes.main : [],
              numbers
            );
          }
        );
      }
    );
  }

  // Finish pipeline for the cohort path: format each axis, build cards with a
  // deterministic "Shares the letter/number …" reason, interleave the two axes
  // (so the cohort reads as a blend), dedupe, cap. Falls back to the existing
  // pattern search if the cohort yields nothing available.
  private finishDatelessCohort(
    key: string,
    letterRaw: Registration[],
    letters: string,
    numberRaw: Registration[],
    numbers: string
  ): void {
    this.mode = 'cohort';
    const seen = new Set<string>([key]);
    const letterCards = this.toCohortCards(
      this.registrationService.formatRegistrations((letterRaw || []).slice()),
      seen,
      `Shares the letter ${letters}`
    );
    const numberCards = this.toCohortCards(
      this.registrationService.formatRegistrations((numberRaw || []).slice()),
      seen,
      `Shares the number ${numbers}`
    );

    const cards: SimilarCard[] = [];
    const max = Math.max(letterCards.length, numberCards.length);
    for (let i = 0; i < max && cards.length < this.limit; i++) {
      if (i < letterCards.length) cards.push(letterCards[i]);
      if (cards.length >= this.limit) break;
      if (i < numberCards.length) cards.push(numberCards[i]);
    }

    if (!cards.length) {
      // Nothing available in the cohort — fall back to the existing pattern
      // search rather than showing an empty section.
      this.patternFallback(key, false);
      return;
    }

    this.note =
      "Comparable dateless plates for sale that share this registration's letter or number.";
    this.commit(key, cards);
  }

  // Map a formatted cohort result set to cards: exclude self, available only,
  // dedupe across both axes (shared `seen` set), apply the fixed reason label.
  private toCohortCards(
    formatted: Registration[],
    seen: Set<string>,
    reason: string
  ): SimilarCard[] {
    const out: SimilarCard[] = [];
    for (const r of formatted) {
      if (!r || !r.registration) continue;
      const rc = r.registration.replace(/\s+/g, '').toUpperCase();
      if (seen.has(rc)) continue; // excludes self (seeded) + dedupes
      if (!(r.price >= 0)) continue; // available/listed only
      seen.add(rc);
      out.push({
        registration: r.registration,
        formattedRegistration: r.formattedRegistration || r.registration,
        pricePence: r.price,
        reason,
        term: (this.searchTerm || '').trim() || null,
        styleKey: this.styleKey(r.style),
      });
    }
    return out;
  }

  // Deterministic reason labels only; returns null when nothing can be stated
  // safely.
  private reasonFor(
    rk: string | null,
    mode: 'search' | 'format'
  ): string | null {
    if (mode === 'search') return 'Same search';
    return this.reasonForStyle(rk);
  }

  // Format-mode label derived purely from the result's style key vs. our
  // formatKey input. Returns null (omitted) when the format isn't known.
  private reasonForStyle(rk: string | null): string | null {
    if (this.formatKey && rk && rk === this.formatKey) {
      return this.formatKey === 'dateless'
        ? 'Similar dateless format'
        : 'Same format';
    }
    return null;
  }

  private styleKey(style: string | null | undefined): string | null {
    const s = (style || '').toLowerCase();
    if (s.includes('dateless')) return 'dateless';
    if (s.includes('prefix')) return 'prefix';
    if (s.includes('suffix')) return 'suffix';
    if (s.includes('current')) return 'current';
    return null;
  }

  public openCard(card: SimilarCard): void {
    const path = card.term
      ? `/plate-detail-v2/${card.registration}/${card.term}`
      : `/plate-detail-v2/${card.registration}`;
    this.router.navigate([path]);
  }

  public browseAll(): void {
    const term =
      (this.searchTerm || '').trim() || this.compactReg;
    if (term) this.router.navigate([`/search-results/${term}`]);
  }
}
